import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import InfoIcon from "../../../icons/Info";
import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import {
  useGetScaleOptions,
  useGetTemplateOptionsForScale,
} from "../../../hooks/queries/useSpecQueries";
import { useCreateCondition } from "../../../hooks/mutations/useSpecMutations";

const ModalContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const ModalHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const ModalFooter = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

type AddNewConditionModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: NewConditionData) => void;
  initialData?: Partial<NewConditionData>;
};

const AddNewConditionModal = ({
  open,
  onClose,
  onSubmit,
  initialData,
}: AddNewConditionModalProps) => {
  const { exp_id } = useParams();
  const createConditionMutation = useCreateCondition();

  const [formData, setFormData] = useState<NewConditionData>({
    scale: "",
    template_uuid: "",
    base_item_uuid: "",
    name: "",
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof NewConditionData, string>>
  >({});

  // Fetch scale options from FormSpec
  const {
    data: scaleOptionsData,
    isLoading: isLoadingScaleOptions,
    error: scaleOptionsError,
  } = useGetScaleOptions();

  // Fetch templates for the selected scale (only when scale is selected)
  const {
    data: templatesData,
    isLoading: isLoadingTemplates,
    error: templatesError,
  } = useGetTemplateOptionsForScale(formData.scale);

  // Reset form when modal opens, use initialData if provided
  useEffect(() => {
    if (open) {
      setFormData({
        scale: initialData?.scale || "",
        template_uuid: initialData?.template_uuid || "",
        base_item_uuid: initialData?.base_item_uuid || "",
        name: initialData?.name || "",
      });
      setErrors({});
    }
  }, [open, initialData]);

  // Template options for the selected scale (filtered by the backend)
  const availableTemplates = useMemo((): TemplateOption[] => {
    if (!formData.scale || !templatesData?.data) return [];

    return templatesData.data.map(
      (template: any): TemplateOption => ({
        uuid: template.uuid,
        name: template.name,
        base_item_uuid: template.base_item_uuid,
        description: template.description,
      })
    );
  }, [formData.scale, templatesData]);

  const handleInputChange = (
    field: keyof NewConditionData,
    value: string | number
  ) => {
    setFormData((prev: NewConditionData) => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev: Partial<Record<keyof NewConditionData, string>>) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };
  const handleScaleChange = (event: SelectChangeEvent) => {
    const newScale = event.target.value;
    if (newScale === formData.scale) return;

    // Templates are scale-specific, so drop any selection made for the
    // previous scale immediately — otherwise a stale template could be
    // submitted while the new list loads or when the new scale has none.
    setFormData((prev: NewConditionData) => ({
      ...prev,
      scale: newScale,
      template_uuid: "",
      base_item_uuid: "",
    }));
    setErrors((prev: Partial<Record<keyof NewConditionData, string>>) => ({
      ...prev,
      scale: undefined,
      template_uuid: undefined,
    }));
  };

  const handleTemplateChange = (event: SelectChangeEvent) => {
    const selectedTemplateUuid = event.target.value;
    const selectedTemplate = availableTemplates.find(
      (t: TemplateOption) => t.uuid === selectedTemplateUuid
    );

    setFormData((prev: NewConditionData) => ({
      ...prev,
      template_uuid: selectedTemplateUuid,
      base_item_uuid: selectedTemplate?.base_item_uuid || "",
    }));

    // Clear error when user selects a template
    if (errors.template_uuid) {
      setErrors((prev: Partial<Record<keyof NewConditionData, string>>) => ({
        ...prev,
        template_uuid: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof NewConditionData, string>> = {};

    if (!formData.scale) {
      newErrors.scale = "Scale is required";
    }

    if (!formData.template_uuid) {
      newErrors.template_uuid = "Template is required";
    }

    if (!formData.name.trim()) {
      newErrors.name = "Condition name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      try {
        const response = await createConditionMutation.mutateAsync({
          ...formData,
          exp_id: parseInt(exp_id || "0"),
        });

        const createdConditionId = response?.cell_condition_id;
        const dataToSubmit = createdConditionId
          ? {
              ...formData,
              createdConditionId: createdConditionId,
            }
          : formData;
        onSubmit(dataToSubmit);

        setFormData({
          scale: "",
          template_uuid: "",
          base_item_uuid: "",
          name: "",
        });
        setErrors({});

        onClose();
      } catch (error) {
        console.error("Error creating condition:", error);
      }
    }
  };

  const handleCancel = () => {
    setFormData({
      scale: "",
      template_uuid: "",
      base_item_uuid: "",
      name: "",
    });
    setErrors({});
    onClose();
  };

  return (
    <Modal open={open} onClose={handleCancel}>
      <ModalContent>
        <ModalHeader>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6">Add New Condition</Typography>
            <Tooltip
              title={
                <Box>
                  <Typography variant="body2">
                    If you need to use the old Oak specification flow, change
                    the URL to "/experiments/&lt;exp_id&gt;/conditions/new".
                    **This should only be used as a last resort**
                  </Typography>
                </Box>
              }
              arrow
            >
              <IconButton size="small">
                <InfoIcon style={{ width: "20px", height: "20px" }} />
              </IconButton>
            </Tooltip>
          </Box>
        </ModalHeader>

        <Box sx={{ width: "100%" }}>
          <Typography
            component="label"
            sx={{
              display: "block",
              fontSize: "16px",
              fontWeight: 400,
              color: "#333",
              mb: 1.5,
            }}
          >
            Scale
            <Typography component="span" sx={{ color: "#d32f2f", ml: 0.5 }}>
              *
            </Typography>
          </Typography>
          <FormControl fullWidth error={!!errors.scale || !!scaleOptionsError}>
            <Select
              value={formData.scale}
              onChange={handleScaleChange}
              disabled={isLoadingScaleOptions || !!scaleOptionsError}
            >
              {(scaleOptionsData?.data || []).map((scale: string) => (
                <MenuItem key={scale} value={scale}>
                  {scale}
                </MenuItem>
              ))}
            </Select>
            {scaleOptionsError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                Failed to load scale options: {scaleOptionsError.message}
              </Typography>
            )}
            {errors.scale && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {errors.scale}
              </Typography>
            )}
          </FormControl>
        </Box>

        <Box sx={{ width: "100%" }}>
          <Typography
            component="label"
            sx={{
              display: "block",
              fontSize: "16px",
              fontWeight: 400,
              color: "#333",
              mb: 1.5,
            }}
          >
            Template
            <Typography component="span" sx={{ color: "#d32f2f", ml: 0.5 }}>
              *
            </Typography>
          </Typography>
          <FormControl
            fullWidth
            disabled={!formData.scale}
            error={!!errors.template_uuid || !!templatesError}
          >
            <Select
              value={formData.template_uuid}
              onChange={handleTemplateChange}
            >
              {isLoadingTemplates ? (
                <MenuItem disabled>Loading templates...</MenuItem>
              ) : templatesError ? (
                <MenuItem disabled>Failed to load templates</MenuItem>
              ) : availableTemplates.length === 0 ? (
                <MenuItem disabled>No templates for this scale</MenuItem>
              ) : (
                availableTemplates.map((template: TemplateOption) => (
                  <MenuItem key={template.uuid} value={template.uuid}>
                    {template.name}
                  </MenuItem>
                ))
              )}
            </Select>
            {templatesError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                Failed to load templates: {templatesError.message}
              </Typography>
            )}
            {errors.template_uuid && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {errors.template_uuid}
              </Typography>
            )}
          </FormControl>
        </Box>

        <Box sx={{ width: "100%" }}>
          <Typography
            component="label"
            sx={{
              display: "block",
              fontSize: "16px",
              fontWeight: 400,
              color: "#333",
              mb: 1.5,
            }}
          >
            Condition Name
            <Typography component="span" sx={{ color: "#d32f2f", ml: 0.5 }}>
              *
            </Typography>
          </Typography>
          <TextField
            fullWidth
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            placeholder="Enter condition name"
          />
        </Box>

        <ModalFooter>
          <Button
            color="secondary"
            onClick={handleCancel}
            disabled={createConditionMutation.isPending}
          >
            CANCEL
          </Button>
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={
              createConditionMutation.isPending ||
              (!!formData.scale && isLoadingTemplates)
            }
          >
            {createConditionMutation.isPending
              ? "CREATING..."
              : "CREATE CONDITION"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddNewConditionModal;
